import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import styles from './markdown.scss';

function normalizeNewlines(input) {
  if (typeof input !== 'string') return input;
  return input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function splitParagraphs(input) {
  return input.split(/\n{2,}/g).filter((p) => p.length > 0);
}

function parseInlineSimpleMarkdown(text) {
  // Small subset: links [text](url), bold **text**, italics *text* or _text_
  const elements = [];

  function processRegex(sourceStr, regex, mapMatchToNode) {
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(sourceStr)) !== null) {
      if (match.index > lastIndex) {
        parts.push(sourceStr.slice(lastIndex, match.index));
      }
      parts.push(mapMatchToNode(match));
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < sourceStr.length) {
      parts.push(sourceStr.slice(lastIndex));
    }
    return parts;
  }

  // Links first
  let interim = processRegex(
    text,
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (m) => (
      <a
        key={`a-${m.index}`}
        href={m[2]}
        target="_blank"
        rel="noopener noreferrer"
      >
        {m[1]}
      </a>
    )
  );
  // Bold: support **text** and __text__
  interim = interim.flatMap((chunk, i) => {
    if (typeof chunk !== 'string') return [chunk];
    return processRegex(chunk, /(\*\*([^*]+)\*\*|__([^_]+)__)/g, (m) => (
      <strong key={`b-${i}-${m.index}`}>{m[2] || m[3]}</strong>
    ));
  });
  // Italics (only asterisk-based to avoid treating underscores as emphasis)
  interim = interim.flatMap((chunk, i) => {
    if (typeof chunk !== 'string') return [chunk];
    return processRegex(chunk, /\*([^*]+)\*/g, (m) => {
      const content = m[1] || '';
      return <em key={`i-${i}-${m.index}`}>{content}</em>;
    });
  });

  interim.forEach((c) => elements.push(c));
  return elements;
}

function renderDisplayMarkdown(value) {
  const normalized = normalizeNewlines(value || '');
  const paragraphs = splitParagraphs(normalized);
  return paragraphs.map((p, idx) => {
    const lines = p.split(/\n/g);
    const children = [];
    lines.forEach((line, i) => {
      const inlineNodes = parseInlineSimpleMarkdown(line);
      inlineNodes.forEach((n) => children.push(n));
      if (i < lines.length - 1) {
        children.push(<br key={`br-${idx}-${i}`} />);
      }
    });
    return <p key={`p-${idx}`}>{children}</p>;
  });
}

class Markdown extends React.Component {
  getClassNames() {
    const { className, viewAsTextarea } = this.props;

    return classNames({
      [styles.markdown]: true,
      [styles.viewAsTextarea]: viewAsTextarea,
      [styles.viewAsDisplayContent]: !viewAsTextarea,
      [className]: !!className,
    });
  }

  render() {
    const { viewAsTextarea, value, onChange, placeholder } = this.props;

    if (!viewAsTextarea) {
      return (
        <div className={this.getClassNames()} suppressHydrationWarning>
          {renderDisplayMarkdown(value)}
        </div>
      );
    }

    return (
      <div className={this.getClassNames()} suppressHydrationWarning>
        <textarea
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          rows={8}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            boxShadow: 'none',
          }}
        />
      </div>
    );
  }
}

Markdown.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
  handleTabChange: PropTypes.func,
  initialTab: PropTypes.string,
  viewAsTextarea: PropTypes.bool,
  placeholder: PropTypes.string,
  disallowedTypes: PropTypes.array,
};

export default Markdown;
